import { useReducer, useEffect, createContext } from 'react';
import type { ReactNode } from 'react';
import type { User, LoginRequest, AuthState } from '../types/index';
import { apiService } from '../services/api';

// Auth Context Types
export interface AuthContextType {
  state: AuthState;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshAuth: () => Promise<void>;
}

export type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

export const normalizeUser = (userData: any): User => {
  const roleData = userData?.role;
  const isRoleString = typeof roleData === 'string';
  const roleId = isRoleString 
    ? 1 
    : ((roleData as any)?.id || (userData as any)?.role_id || 1);
  
  const unitId = userData?.unit?.id || (userData as any)?.unit_id;

  return {
    id: userData?.id || 0,
    username: userData?.username || '',
    email: userData?.email || '',
    firstName: userData?.firstName || userData?.full_name?.split(' ')[0] || '',
    lastName: userData?.lastName || userData?.full_name?.split(' ').slice(1).join(' ') || '',
    full_name: userData?.full_name || `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim() || userData?.username || '',
    roleId,
    role_id: roleId,
    unitId,
    unit_id: unitId,
    role: roleData ? (
      !isRoleString && typeof roleData === 'object' ? {
        id: (roleData as any).id || roleId,
        name: (roleData as any).name || '',
        description: (roleData as any).description,
        permissions: (roleData as any).permissions,
      } : {
        id: roleId,
        name: String(roleData),
        description: undefined,
        permissions: []
      }
    ) : undefined,
    unit: userData?.unit ? {
      id: userData.unit.id || unitId || 0,
      name: userData.unit.name || '',
      code: userData.unit.code,
      description: (userData.unit as any).description,
    } : undefined,
    department: (userData as any)?.department ? (
      typeof (userData as any).department === 'object' ? {
        id: (userData as any).department.id,
        name: (userData as any).department.name
      } : { id: 0, name: (userData as any).department }
    ) : undefined,
    job_title: (userData as any)?.job_title,
    isActive: userData?.isActive !== undefined ? userData.isActive : (userData?.is_active !== undefined ? userData.is_active : true),
    is_active: userData?.isActive !== undefined ? userData.isActive : (userData?.is_active !== undefined ? userData.is_active : true),
    lastLogin: (userData as any)?.last_login || (userData as any)?.lastLogin,
    last_login: (userData as any)?.last_login || (userData as any)?.lastLogin,
    createdAt: userData?.createdAt || userData?.created_at || new Date().toISOString(),
    created_at: userData?.created_at || userData?.createdAt || new Date().toISOString(),
    updatedAt: userData?.updatedAt || userData?.updated_at || new Date().toISOString(),
    updated_at: userData?.updated_at || userData?.updatedAt || new Date().toISOString(),
    profile_picture: (userData as any)?.profile_picture,
    permissions: (userData as any)?.permissions || [],
  };
};

export const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
};

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState, (defaultState) => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('access_token');
      if (storedUser && token) {
        // Parse and normalize the stored user data to ensure consistency (e.g. role object vs string)
        const parsedUser = JSON.parse(storedUser);
        const user = normalizeUser(parsedUser);
        
        return {
          ...defaultState,
          user,
          token,
          isAuthenticated: true,
          loading: false,
        };
      }
    } catch (error) {
      console.error('Failed to restore auth state:', error);
    }
    return defaultState;
  });

  // Check for existing token on app start and restore session
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (token && refreshToken) {
          try {
            // Verify token is still valid by fetching current user
            const response = await apiService.getCurrentUser();
            if (response.data?.success && response.data?.data) {
              const userData = response.data.data;
              
              // Transform user data to match User type
              const user: User = normalizeUser(userData);
              
              // Update storage with latest user data
              localStorage.setItem('user', JSON.stringify(user));

              dispatch({
                type: 'LOGIN_SUCCESS',
                payload: {
                  user: user,
                  token: token,
                },
              });
              return; // Success, exit early
            }
          } catch (error: any) {
            // Any error means auth failed - let api interceptor handle refresh
            // Just clear local state and let user login again
            console.warn('Auth check failed:', error.response?.status || error.message);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            dispatch({ type: 'LOGIN_FAILURE' });
          }
        } else {
          // No tokens found
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Auth check error:', error);
        dispatch({ type: 'LOGIN_FAILURE' });
      } finally {
        // Always stop loading after check completes
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      
      const response = await apiService.login(credentials);
      
      // Backend returns: { success, message, data: { user, access_token, refresh_token } }
      const responseData = response.data;
      const loginData = responseData.data; // Extract data from wrapped response
      
      if (!loginData || !loginData.access_token || !loginData.refresh_token || !loginData.user) {
        throw new Error('Invalid login response structure');
      }
      
      const { access_token, refresh_token, user: userData } = loginData;
      
      if (!access_token || !refresh_token || !userData) {
        throw new Error('Invalid login response structure');
      }
      
      // Transform user data to match User type
      const user = normalizeUser(userData);

      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: user, token: access_token },
      });
    } catch (error: any) {
      console.error('Login error:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    // Clear state and storage immediately so UI shows logged-out state without waiting for API
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    dispatch({ type: 'LOGOUT' });
    // Notify server in background (do not block navigation)
    apiService.logout().catch((error) => {
      console.error('Logout API error:', error);
    });
  };

  const updateUser = (user: User) => {
    if (!state.user) return;
    const merged: User = {
      ...state.user,
      ...user,
      roleId: (user as any).roleId ?? state.user.roleId ?? (user.role as any)?.id ?? (state.user.role as any)?.id ?? 1,
      role_id: (user as any).role_id ?? state.user.role_id ?? (user.role as any)?.id ?? (state.user.role as any)?.id ?? 1,
    };
    dispatch({ type: 'UPDATE_USER', payload: merged });
    localStorage.setItem('user', JSON.stringify(merged));
  };

  const refreshAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await apiService.getCurrentUser();
      if (response.data?.success && response.data?.data) {
        const userData = response.data.data;
        const user = normalizeUser(userData);
        localStorage.setItem('user', JSON.stringify(user));
        dispatch({ type: 'UPDATE_USER', payload: user });
      }
    } catch {
      // Silent fail - token may have expired
    }
  };

  const value: AuthContextType = {
    state,
    login,
    logout,
    updateUser,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;